import React, { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import PageContainer from '../../components/structural/PageContainer';
import { Typography } from '@mui/material';
import BoxInputContainer from '../../components/custom/BoxInputContainer';
import CustomPrompt from '../../components/custom/CustomPrompt';
import { useCreateJobsMutation, useScheduleJobsMutation } from '../../redux/RTK/mutations/jobsMutations';
import { useGetProjectsQuery } from '../../redux/RTK/queries/projectQuery';
import { useAddProjectFileMutation } from '../../redux/RTK/mutations/projectFileMutations';
import { FileType } from '../../models/inputTypes/FileType';
import { useAppDispatch } from '../../redux/store';
import { jstNumericalInitialState, jstSubmissionInitialState } from '../../utils/initialStates/jstInitialStates';
import useSimulationPreparation from '../../utils/hooks/jsonParamHook';
import { openWarningToast } from '../../redux/slices/appSlice';
import { ModelEnum } from '../../models/types/ModelEnum';
import { JstNumerical, JstSubmission } from '../../models/inputTypes/JstFields';
import JstDataSelectionGroup from '../../components/groups/jst/JstDataSelectionGroup';
import JstSubmissionInputsGroup from '../../components/groups/jst/JstSubmissionInputsGroup';
import { JstSimulationPreparationResponse } from '../../utils/simulationFileToState/jstSimulationPreparation';
import { jstPreparation } from '../../utils/modelPreparation/jstPreparation';

const MarisJstPage: FC = () => {
  const [createJob] = useCreateJobsMutation();
  const { data } = useGetProjectsQuery({});
  const [uploadFile] = useAddProjectFileMutation();
  const dispatch = useAppDispatch();

  const [numericalState, setNumericalState] = useState<JstNumerical>(jstNumericalInitialState);
  const [submissionState, setSubmissionState] = useState<JstSubmission>(jstSubmissionInitialState);
  const [stateHasChanged, setStateHasChanged] = useState(false);
  const { preparedData, error, currentFile } = useSimulationPreparation(ModelEnum.JST);

  useEffect(() => {
    if (preparedData && !error && currentFile) {
      onSimulationFileChange(preparedData, currentFile);
    }
  }, [preparedData, error, currentFile]);

  useEffect(() => {
    if (!stateHasChanged) {
      const hasNumericalChanged = numericalState != jstNumericalInitialState;
      const hasSubChanged = submissionState != jstSubmissionInitialState;
      setStateHasChanged(hasNumericalChanged || hasSubChanged);
    }
  }, [numericalState, submissionState]);

  const selectedProjectName = useMemo(() => {
    if (data?.length) {
      const found = data.find((item) => item.id.toString() === numericalState.project.toString());
      return found?.name;
    }
  }, [numericalState.project]);

  const onSubmissionClick = (event: FormEvent<any>) => {
    event.preventDefault();
    const preparedValue = jstPreparation(numericalState, submissionState);
    if (preparedValue) createJob(preparedValue);
  };

  const onSimulationSave = () => {
    console.log('save simulation file');
    const preparedValue = jstPreparation(numericalState, submissionState);
    if (preparedValue && numericalState.project && submissionState?.simulationFile) {
      uploadFile({
        id: numericalState.project,
        name: `${submissionState?.simulationFile}.json`,
        file: preparedValue,
      });
    } else {
      dispatch(
        openWarningToast(
          !numericalState.project ? 'You have to select project first!' : 'simulation file name required!',
        ),
      );
    }
  };

  const onSimulationFileChange = (value: JstSimulationPreparationResponse, file?: FileType) => {
    setNumericalState({ ...value.numeric, simulationSetup: file });
    setSubmissionState({ ...value.submission, simulationFile: file?.fileName?.split('.json')[0] ?? '' });
  };

  return (
    <PageContainer title={'NUMERICAL TOOLS'} subTitle={'Maris JST'} projectName={selectedProjectName}>
      <form onSubmit={onSubmissionClick}>
        <CustomPrompt
          when={stateHasChanged}
          message={(params) =>
            params.pathname == '/designTools/maris-jst' ? true : 'Changes that you made may not be saved.'
          }
        />
        <Typography variant={'h4'}>Data Selection</Typography>
        <BoxInputContainer borderRadius={'4px'} mt={2}>
          <JstDataSelectionGroup
            simulationChangeFile={onSimulationFileChange}
            inputState={numericalState}
            setInputState={setNumericalState}
          />
        </BoxInputContainer>

        <Typography mt={2} variant={'h4'}>
          Submission
        </Typography>
        <BoxInputContainer borderRadius={'4px'} mt={2}>
          <JstSubmissionInputsGroup
            onSaveClick={onSimulationSave}
            inputState={submissionState}
            setInputState={setSubmissionState}
          />
        </BoxInputContainer>
      </form>
    </PageContainer>
  );
};

export default MarisJstPage;
